/**
 * Money — integer minor units, end to end.
 *
 * Every monetary value in this system is a whole number of minor units
 * (centavos for PHP). Floating point never touches money: not in IndexedDB, not
 * in D1, not in JSON, not in a computation. A `Money` serialises as a plain
 * number, so it crosses the wire and the storage boundary without ceremony.
 *
 * Rounding is HALF AWAY FROM ZERO at the minor unit, applied only at the points
 * the pricing engine documents. `allocate` exists so that apportioning a total
 * can never lose or invent a centavo.
 *
 * Constitution Principle II and VII. Tests: tests/unit/money.test.ts.
 */

/** Branded so a raw number cannot be passed where an audited amount is required. */
export type Money = number & { readonly __money: unique symbol };

export class MoneyError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "MoneyError";
  }
}

/** PHP. Sourced from BrandConfig at the edges; fixed here for arithmetic. */
const MINOR_UNITS = 2;

export const ZERO = 0 as Money;

// ---------------------------------------------------------------------------
// Construction
// ---------------------------------------------------------------------------

/** Build a Money from a whole number of minor units. */
export function money(minorUnits: number): Money {
  if (typeof minorUnits !== "number" || Number.isNaN(minorUnits)) {
    throw new MoneyError(`Not a number: ${String(minorUnits)}`);
  }
  if (!Number.isFinite(minorUnits)) {
    throw new MoneyError(`Not finite: ${minorUnits}`);
  }
  if (!Number.isInteger(minorUnits)) {
    throw new MoneyError(
      `Fractional minor unit: ${minorUnits}. A fraction of a centavo is not an amount that exists.`,
    );
  }
  if (!Number.isSafeInteger(minorUnits)) {
    throw new MoneyError(`Outside safe integer range: ${minorUnits}`);
  }
  return minorUnits as Money;
}

/**
 * Build a Money from major units (₱285.50 → 28550).
 *
 * Deliberately NOT `Math.round(value * 100)`. That fails on values a till hits
 * constantly: 1.005 * 100 is 100.49999999999999 in IEEE 754, which would round
 * down to ₱1.00 and quietly under-charge. Instead the value is taken to its
 * shortest faithful decimal form and the point is shifted textually, so the
 * rounding decision is made on the digit the customer would actually read.
 */
export function fromMajor(value: number, minorUnits: number = MINOR_UNITS): Money {
  if (typeof value !== "number" || Number.isNaN(value) || !Number.isFinite(value)) {
    throw new MoneyError(`Not a finite number: ${String(value)}`);
  }

  const negative = value < 0;
  const magnitude = Math.abs(value);

  // 15 significant digits is the widest a double represents faithfully, so this
  // strips binary representation noise without inventing precision.
  let text = magnitude.toPrecision(15);
  if (text.includes("e") || text.includes("E")) {
    // Beyond fixed-notation range; fall back and let money() reject if it is
    // genuinely too large to be an amount.
    text = magnitude.toFixed(minorUnits + 6);
  }

  const dot = text.indexOf(".");
  const wholeDigits = dot === -1 ? text : text.slice(0, dot);
  let fracDigits = dot === -1 ? "" : text.slice(dot + 1);

  // Need one digit beyond the minor unit to decide the rounding.
  while (fracDigits.length < minorUnits + 1) fracDigits += "0";

  const kept = wholeDigits + fracDigits.slice(0, minorUnits);
  const decidingDigit = fracDigits.charCodeAt(minorUnits) - 48;

  let result = Number(kept);
  if (decidingDigit >= 5) result += 1;

  return money(negative ? -result : result);
}

/** For display only. Never feed this back into arithmetic. */
export function toMajor(amount: Money, minorUnits: number = MINOR_UNITS): number {
  return amount / 10 ** minorUnits;
}

// ---------------------------------------------------------------------------
// Arithmetic
// ---------------------------------------------------------------------------

export function add(a: Money, b: Money): Money {
  return money(a + b);
}

export function sub(a: Money, b: Money): Money {
  return money(a - b);
}

/** Multiply by a line quantity. Quantities are whole; you sell 2 or 3, never 2.5. */
export function mul(amount: Money, quantity: number): Money {
  if (!Number.isInteger(quantity)) {
    throw new MoneyError(`Quantity must be a whole number, got ${quantity}`);
  }
  return money(amount * quantity);
}

/**
 * Apply a rate expressed in basis points (1200 = 12%, 2000 = 20%).
 *
 * Rates are basis points rather than floats so a tax rate is itself an exact
 * integer — `0.12` cannot be represented exactly, `1200` always can. The
 * division is done with an explicit remainder so the half-way case rounds
 * deterministically rather than inheriting whatever the FPU decides.
 */
export function mulRate(amount: Money, basisPoints: number): Money {
  if (!Number.isInteger(basisPoints)) {
    throw new MoneyError(`Basis points must be a whole number, got ${basisPoints}`);
  }
  if (basisPoints < 0) {
    throw new MoneyError(`Negative rate: ${basisPoints}. Express a reduction as a discount.`);
  }

  const scaled = amount * basisPoints;
  const quotient = Math.trunc(scaled / 10_000);
  const remainder = Math.abs(scaled % 10_000);

  // Half away from zero.
  if (remainder * 2 >= 10_000) {
    return money(quotient + (scaled < 0 ? -1 : 1));
  }
  return money(quotient);
}

export function sum(amounts: readonly Money[]): Money {
  let total = 0;
  for (const a of amounts) total += a;
  return money(total);
}

export function cmp(a: Money, b: Money): number {
  return a - b;
}

export function isNegative(amount: Money): boolean {
  return amount < 0;
}

export function isZero(amount: Money): boolean {
  return amount === 0;
}

export function max(a: Money, b: Money): Money {
  return a >= b ? a : b;
}

export function min(a: Money, b: Money): Money {
  return a <= b ? a : b;
}

/** Clamp at zero. Used wherever a discount must never produce a negative line. */
export function clampAtZero(amount: Money): Money {
  return amount < 0 ? ZERO : amount;
}

// ---------------------------------------------------------------------------
// Allocation
// ---------------------------------------------------------------------------

/**
 * Split an amount across weighted parts so the parts sum EXACTLY to the whole.
 *
 * This is the largest-remainder method. It matters because the obvious
 * implementation — multiply each share and round — loses or invents centavos,
 * and a receipt whose lines do not add up to its total is the defect customers
 * notice first and trust least.
 *
 * Used for shared-bill statutory discounts (FR-030) and any line-level
 * apportionment.
 */
export function allocate(amount: Money, weights: readonly number[]): Money[] {
  if (weights.length === 0) {
    throw new MoneyError("Cannot allocate across zero parts");
  }
  for (const w of weights) {
    if (!Number.isFinite(w) || w < 0) {
      throw new MoneyError(`Weights must be finite and non-negative, got ${w}`);
    }
  }

  const totalWeight = weights.reduce((a, b) => a + b, 0);
  if (totalWeight <= 0) {
    throw new MoneyError("Total weight must be greater than zero");
  }

  const negative = amount < 0;
  const magnitude = Math.abs(amount);

  const base = weights.map((w) => Math.floor((magnitude * w) / totalWeight));
  const distributed = base.reduce((a, b) => a + b, 0);
  let remainder = magnitude - distributed;

  // Whoever was cut by the most gets the spare unit first. Ties break by
  // position so the result is deterministic and reproducible on a reprint.
  const order = weights
    .map((w, index) => ({ index, fraction: (magnitude * w) % totalWeight }))
    .sort((a, b) => b.fraction - a.fraction || a.index - b.index);

  const result = [...base];
  let cursor = 0;
  while (remainder > 0) {
    const target = order[cursor % order.length]!;
    result[target.index]! += 1;
    remainder -= 1;
    cursor += 1;
  }

  return result.map((value) => money(negative ? -value : value));
}
